import React, { useState, useEffect } from "react";
import { bool, func, string, object, number } from "prop-types";
import { useTranslation } from "@lib/i18n";

import getDebug from "debug";
import produce from "immer";
import equal from "fast-deep-equal";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import getConfig from "next/config";
import { useRouter } from "next/router";

import CustomerConsents from "./CustomerConsents";
import Divider from "@components/shared/Divider";
import Checkbox from "@components/shared/Checkbox";
import StickyRequestButton from "@components/shared/StickyRequestButton";
import CompanyInformation from "@components/UserDetails/CompanyInformation";
import PersonalInformation from "@components/UserDetails/PersonalInformation";
import Address from "@components/UserDetails/Address";
import Modal from "@components/shared/Modal";
import Typography from "@components/shared/Typography";

import api from "@api";

import {
  BRANDID_RESPONSES,
  COUNTRIES,
  CTA_SOL_TYPES,
  CTA_SOL_TYPES_ORDER,
  CUSTOM_EVENT,
  USER_FIELDS_NAMES,
} from "@shared/constants";
import { getBrandIdLoginRedirect } from "@lib/getBrandIdLoginRedirect";
import { toast } from "@lib/reactToastify";
import { getDeliveryDealerForPostcode } from "@lib/getDeliveryDealerForPostcode";
import {
  setDeliveryDealerAction,
  setRegistrationPriceAction,
} from "@redux/actions/dealActions";
import { useGetShouldDeliveryDealerBeIncluded } from "@hooks/useGetShouldDeliveryDealerBeIncluded";
import { useCanCitroenHandleRegistrationPostalCode } from "@hooks/useCanCitroenHandleRegistrationPostalCode";
import { getIsProvinceRelevantForCountry } from "@shared/v2/lib/getIsProvinceRelevantForCountry";
import { getIsTVARelevantForCountry } from "@shared/v2/lib/getIsTVARelevantForCountry";
import { getIsTINRelevantForCountry } from "@shared/v2/lib/getIsTINRelevantForCountry";

import {
  useBrandIdSettingsSelector,
  useCarteGriseConfigSelector,
  useIsReversedDeliveryFieldsOrderSelector,
  useRegexPatternsSelector,
} from "@redux/reducers/appConfig";
import { useDealSelector, useLastCtaSelector } from "@redux/reducers/deal";

const { publicRuntimeConfig } = getConfig();
const { COUNTRY } = publicRuntimeConfig;

const debug = getDebug("app:UserDetailsContainer");

const {
  COMPANY_INFORMATION,
  PERSONAL_INFORMATION,
  ADDRESS,
} = USER_FIELDS_NAMES;

const optionalFields = [
  ADDRESS.ADDITIONAL_ADDRESS,
  COMPANY_INFORMATION.TVA_NUMBER,
];

const getIsDataValid = (data, regexPatterns, country) => {
  const isProvinceRelevantForCountry = getIsProvinceRelevantForCountry(COUNTRY);

  // APP-19031 Add Siret number as optional field for UK
  const optionals =
    country == COUNTRIES.GB
      ? [...optionalFields, COMPANY_INFORMATION.SIRET_NUMBER]
      : optionalFields;

  return Object.entries(data).every(([key, value]) => {
    if (key === ADDRESS.PROVINCE && !isProvinceRelevantForCountry) {
      return true;
    }
    return value
      ? new RegExp(regexPatterns[key]).test(value)
      : optionals.includes(key);
  });
};

const getIsDataEmpty = data =>
  !Object.values(data).some(value => Boolean(value));

const getAreAddressesIdentical = (address1, address2) => {
  if (Object.keys(address1).length !== Object.keys(address2).length) {
    return false;
  }

  return Object.entries(address1).every(([key, value]) => {
    return value === address2[key];
  });
};

const getCurrentBillingAddress = ({
  isDeliveryAddressVisible,
  isBillingAddressSame,
  deliveryAddressData,
  billingAddressData,
}) => {
  if (isDeliveryAddressVisible && isBillingAddressSame) {
    return deliveryAddressData;
  }

  return billingAddressData;
};

const getCurrentRegistrationAddress = ({
  isDeliveryAddressVisible,
  isRegistrationAddressSame,
  deliveryAddressData,
  registrationAddressData,
  billingAddressData,
}) => {
  if (!isRegistrationAddressSame) {
    return registrationAddressData;
  }
  if (isDeliveryAddressVisible) {
    return deliveryAddressData;
  }

  return billingAddressData;
};

const isTVARelevantForSelectedCountry = getIsTVARelevantForCountry(COUNTRY);
const isTINRelevantForSelectedCountry = getIsTINRelevantForCountry(COUNTRY);

const UserDetailsContainer = ({
  onSubmitSuccessCallback,
  returnUrlAfterLogin,
  defaultValues,
  onEmailButtonClick,
  onEditingStart,
  withDividers,
  isConfirmationButtonVisible,
  isPersonalInformationEditButtonDisabled,
  confirmationButtonLabel,
  isCompanyInformationVisible,
  isCompanyInformationRequired,
  isDeliveryAddressRequired,
  isDeliveryAddressVisible,
  isRegistrationAddressRequired,
  isRegistrationAddressVisible,
  isBillingAddressRequired,
  isBillingAddressVisible,
  fixedHeaderHeight = 0,
  withConsents,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const dispatch = useDispatch();
  const router = useRouter();

  const lastCta = useLastCtaSelector();
  const deal = useDealSelector();
  const { currentDeal } = deal;

  const carteGriseConfig = useCarteGriseConfigSelector();

  const withCarteGrise =
    carteGriseConfig?.enabled &&
    CTA_SOL_TYPES_ORDER[deal.lastCta] <
      CTA_SOL_TYPES_ORDER[CTA_SOL_TYPES.PERSONAL_DETAILS_CONFIRMED];

  const canCitroenHandleRegistrationPostalCode = useCanCitroenHandleRegistrationPostalCode();

  const { consents: leadConsents = {} } = currentDeal.extendedCustomerInfo;

  const formHook = useForm({ defaultValues: { consents: leadConsents } });
  const { getValues, setError } = formHook;

  const regexSelector = useRegexPatternsSelector();
  const brandIDConfig = useBrandIdSettingsSelector();
  const isReversedOrder = useIsReversedDeliveryFieldsOrderSelector();

  const personalInformationRegexPatterns = {
    [PERSONAL_INFORMATION.USER_FIRST_NAME]: regexSelector.userFirstName,
    [PERSONAL_INFORMATION.USER_LAST_NAME]: regexSelector.userLastName,
    [PERSONAL_INFORMATION.PHONE]: regexSelector.phone,
    [PERSONAL_INFORMATION.TIN]: regexSelector.tin,
  };

  const addressRegexPatterns = {
    [ADDRESS.PRIMARY_ADDRESS]: regexSelector.address,
    [ADDRESS.ADDITIONAL_ADDRESS]: regexSelector.additionalAddress,
    [ADDRESS.CITY]: regexSelector.city,
    [ADDRESS.POST_CODE]: regexSelector.postCode,
    [ADDRESS.COUNTRY]: regexSelector.country,
    [ADDRESS.PROVINCE]: regexSelector.province,
  };

  const companyInformationRegexPatterns = {
    [COMPANY_INFORMATION.COMPANY_NAME]: regexSelector.companyName,
    [COMPANY_INFORMATION.SIRET_NUMBER]: regexSelector.siretNumber,
    [COMPANY_INFORMATION.TVA_NUMBER]: regexSelector.TVANumber,
  };

  const [
    isEditingCompanyInformation,
    setIsEditingCompanyInformation,
  ] = useState(false);

  const [
    isEditingPersonalInformation,
    setIsEditingPersonalInformation,
  ] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isEditingDeliveryAddress, setIsEditingDeliveryAddress] = useState(
    false
  );

  const [isEditingBillingAddress, setIsEditingBillingAddress] = useState(false);

  const [
    isEditingRegistrationAddress,
    setIsEditingRegistrationAddress,
  ] = useState(false);

  const baseCompanyInformationData = {
    companyName: defaultValues.companyName,
    siretNumber: defaultValues.siretNumber,
  };

  const [companyInformationData, setCompanyInformationData] = useState(
    produce(baseCompanyInformationData, draft => {
      if (isTVARelevantForSelectedCountry) {
        draft.TVANumber = defaultValues.TVANumber;
      }
    })
  );

  const basePersonalInformationData = {
    civility: defaultValues.civility,
    userFirstName: defaultValues.userFirstName,
    userLastName: defaultValues.userLastName,
    phone: defaultValues.phone,
  };

  const [personalInformationData, setPersonalInformationData] = useState(
    produce(basePersonalInformationData, draft => {
      if (isTINRelevantForSelectedCountry) {
        draft.tin = defaultValues.tin;
      }
    })
  );

  const [deliveryAddressData, setDeliveryAddressData] = useState({
    address: defaultValues.deliveryAddress1,
    additionalAddress: defaultValues.deliveryAdditionalAddress,
    city: defaultValues.deliveryCity,
    province: defaultValues.deliveryProvince,
    postCode: defaultValues.deliveryPostCode,
    country: defaultValues.deliveryCountry,
  });

  const [billingAddressData, setBillingAddressData] = useState({
    address: defaultValues.billingAddress1,
    additionalAddress: defaultValues.billingAdditionalAddress,
    city: defaultValues.billingCity,
    province: defaultValues.billingProvince,
    postCode: defaultValues.billingPostCode,
    country: defaultValues.billingCountry,
  });

  const [registrationAddressData, setRegistrationAddressData] = useState({
    address: defaultValues.registrationAddress1,
    additionalAddress: defaultValues.registrationAdditionalAddress,
    city: defaultValues.registrationCity,
    province: defaultValues.registrationProvince,
    postCode: defaultValues.registrationPostalCode,
    country: defaultValues.registrationCountry,
  });

  const [isBillingAddressSame, setIsBillingAddressSame] = useState(
    isDeliveryAddressVisible &&
      getAreAddressesIdentical(deliveryAddressData, billingAddressData)
  );

  // TODO: Handling of visibility of registrationAddress when billing address isn't shown
  const [isRegistrationAddressSame, setIsRegistrationAddressSame] = useState(
    getAreAddressesIdentical(
      isDeliveryAddressVisible ? deliveryAddressData : billingAddressData,
      registrationAddressData
    )
  );

  const handleRegistrationCheckboxChange = () => {
    const nextIsRegistrationAddressSame = !isRegistrationAddressSame;

    setIsRegistrationAddressSame(nextIsRegistrationAddressSame);
    setIsEditingRegistrationAddress(!nextIsRegistrationAddressSame);

    onEditingStart?.();
  };

  const onBillingAddressCheckboxChange = () => {
    const nextIsBillingAddressSame = !isBillingAddressSame;

    setIsBillingAddressSame(nextIsBillingAddressSame);
    setIsEditingBillingAddress(!nextIsBillingAddressSame);

    onEditingStart?.();
  };

  const isPersonalInformationInvalid = !getIsDataValid(
    personalInformationData,
    personalInformationRegexPatterns
  );

  const isCompanyInformationInvalid =
    isCompanyInformationVisible &&
    isCompanyInformationRequired &&
    !getIsDataValid(
      companyInformationData,
      companyInformationRegexPatterns,
      COUNTRY
    );

  const isDeliveryAddressInvalid =
    isDeliveryAddressVisible &&
    isDeliveryAddressRequired &&
    !getIsDataValid(deliveryAddressData, addressRegexPatterns);

  const isBillingAddressInvalid =
    isBillingAddressVisible &&
    isBillingAddressRequired &&
    !getIsDataValid(
      getCurrentBillingAddress({
        isDeliveryAddressVisible,
        isBillingAddressSame,
        deliveryAddressData,
        billingAddressData,
      }),
      addressRegexPatterns
    );

  const isRegistrationAddressInvalid =
    isRegistrationAddressVisible &&
    isRegistrationAddressRequired &&
    !getIsDataValid(
      getCurrentRegistrationAddress({
        isRegistrationAddressSame,
        isDeliveryAddressVisible,
        deliveryAddressData,
        registrationAddressData,
        billingAddressData,
      }),
      addressRegexPatterns
    );

  useEffect(() => {
    if (router.query?.isUserUpdateSuccess === "true") {
      toast.success(t("notify.updateUserInfoSuccess"));
    }
  }, []);

  useEffect(() => {
    if (isPersonalInformationInvalid) {
      debug("❗️ Setting PersonalInformation to editing due to invalid data.");

      setIsEditingPersonalInformation(true);
    }

    if (isCompanyInformationInvalid) {
      debug("❗️ Setting CompanyInformation to editing due to invalid data.");

      setIsEditingCompanyInformation(true);
    }

    if (isDeliveryAddressInvalid) {
      debug("❗️ Setting DeliveryAddress to editing due to invalid data.");

      setIsEditingDeliveryAddress(true);
    }

    if (isBillingAddressInvalid) {
      debug("❗️ Setting BillingAddress to editing due to invalid data.");

      if (isDeliveryAddressVisible) {
        setIsBillingAddressSame(false);
      }

      setIsEditingBillingAddress(true);
    }

    if (isRegistrationAddressInvalid) {
      debug("❗️ Setting RegistrationAddress to editing due to invalid data.");

      setIsRegistrationAddressSame(false);
      setIsEditingRegistrationAddress(true);
    }
  }, [
    isPersonalInformationInvalid,
    isCompanyInformationInvalid,
    isDeliveryAddressInvalid,
    isBillingAddressInvalid,
    isRegistrationAddressInvalid,
  ]);

  useEffect(() => {
    setIsRegistrationAddressSame(
      getAreAddressesIdentical(
        isDeliveryAddressVisible ? deliveryAddressData : billingAddressData,
        registrationAddressData
      )
    );

    setIsBillingAddressSame(
      isDeliveryAddressVisible &&
        getAreAddressesIdentical(deliveryAddressData, billingAddressData)
    );
  }, [isDeliveryAddressVisible]);

  useEffect(() => {
    if (!isRegistrationAddressInvalid && isRegistrationAddressSame) {
      setIsEditingRegistrationAddress(false);
    }
  }, [isRegistrationAddressInvalid]);

  useEffect(() => {
    if (!isBillingAddressInvalid && isBillingAddressSame) {
      setIsEditingBillingAddress(false);
    }
  }, [isBillingAddressInvalid]);

  useEffect(() => {
    if (
      isEditingPersonalInformation ||
      isEditingCompanyInformation ||
      isEditingDeliveryAddress ||
      isEditingBillingAddress
    ) {
      const isGtmFireRequested = true;

      onEditingStart?.(isGtmFireRequested);
    }
  }, [
    isEditingCompanyInformation,
    isEditingPersonalInformation,
    isEditingDeliveryAddress,
    isEditingBillingAddress,
    isEditingRegistrationAddress,
  ]);

  const triggerAddressChange = () => {
    setTimeout(() => {
      onEditingStart?.(true);
    }, 300);
  };

  useEffect(() => {
    const listener = () => {
      triggerAddressChange();
    };

    window.addEventListener(CUSTOM_EVENT.TRIGGER_ADDRESS_CHANGE, listener);

    return () =>
      window.removeEventListener(CUSTOM_EVENT.TRIGGER_ADDRESS_CHANGE, listener);
  }, [lastCta]);

  const getRegistrationCost = async postalCode => {
    try {
      const { data } = await api.getRegistrationCost(postalCode);

      if (data?.data?.registrationCost !== undefined) {
        return {
          deliveryCost: carteGriseConfig.deliveryCost,
          fixedTaxAmount: carteGriseConfig.fixedTaxAmount,
          registrationCost: data.data.registrationCost,
        };
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const onSubmit = async () => {
    const { consents } = getValues();

    if (withConsents) {
      let errorFound = false;

      Object.keys(consents).forEach(consent => {
        if (
          !(consent in consents) ||
          !["false", "true"].includes(String(consents[consent]))
        ) {
          errorFound = true;
          setError(`consents.${consent}`, true);
        }
      });

      if (errorFound) {
        return;
      }
    }

    const isRegistrationAddressOptional =
      isRegistrationAddressVisible && !isRegistrationAddressRequired;

    const currentRegistrationAddressData = getCurrentRegistrationAddress({
      isDeliveryAddressVisible,
      isRegistrationAddressSame,
      deliveryAddressData,
      registrationAddressData,
      billingAddressData,
    });

    const isRegistrationAddressDataEmpty = getIsDataEmpty(
      currentRegistrationAddressData
    );

    const shouldFetchCarteGriseCost =
      withCarteGrise &&
      canCitroenHandleRegistrationPostalCode(
        currentRegistrationAddressData.postCode
      );

    if (shouldFetchCarteGriseCost) {
      const registrationPrice = await getRegistrationCost(
        currentRegistrationAddressData.postCode
      );

      if (registrationPrice === null) {
        toast.error(t("notify.invalidRegistrationPostalCode"));
        return;
      }

      dispatch(setRegistrationPriceAction(registrationPrice));
    }

    const { data: deliveryPoints } = await api.getDeliveryPoints();

    if (isDeliveryAddressRequired && useGetShouldDeliveryDealerBeIncluded()) {
      const deliveryDealerForPostcode = getDeliveryDealerForPostcode(
        deliveryAddressData.postCode,
        deliveryPoints
      );

      if (!deliveryDealerForPostcode) {
        setIsModalOpen(true);

        return;
      }

      dispatch(setDeliveryDealerAction(deliveryDealerForPostcode));
    }

    const isBillingAddressOptional =
      isBillingAddressVisible && !isBillingAddressRequired;

    const currentBillingAddressData = getCurrentBillingAddress({
      isDeliveryAddressVisible,
      isBillingAddressSame,
      deliveryAddressData,
      billingAddressData,
    });

    const isBillingAddressDataEmpty = getIsDataEmpty(currentBillingAddressData);

    const isDeliveryAddressDataEmpty = getIsDataEmpty(deliveryAddressData);

    const isUpdateWithDeliveryAddress =
      isDeliveryAddressRequired ||
      (isDeliveryAddressVisible && !isDeliveryAddressDataEmpty);

    const isUpdateWithBillingAddress =
      isBillingAddressRequired ||
      (isBillingAddressOptional && !isBillingAddressDataEmpty);

    const isUpdateWithRegistrationAddress =
      isRegistrationAddressRequired ||
      (isRegistrationAddressOptional && !isRegistrationAddressDataEmpty);

    const allFields = produce(personalInformationData, draft => {
      if (isCompanyInformationVisible) {
        draft.companyName = companyInformationData.companyName;
        draft.siretNumber = companyInformationData.siretNumber;

        if (isTVARelevantForSelectedCountry) {
          draft.TVANumber = companyInformationData.TVANumber;
        }
      }

      if (isUpdateWithDeliveryAddress) {
        draft.deliveryAddress1 = deliveryAddressData.address;
        draft.deliveryAdditionalAddress = deliveryAddressData.additionalAddress;
        draft.deliveryCity = deliveryAddressData.city;
        draft.deliveryProvince = deliveryAddressData.province;
        draft.deliveryPostCode = deliveryAddressData.postCode;
        draft.deliveryCountry = deliveryAddressData.country;
      }

      if (isUpdateWithBillingAddress) {
        draft.billingAddress1 = currentBillingAddressData.address;
        draft.billingAdditionalAddress =
          currentBillingAddressData.additionalAddress;
        draft.billingCity = currentBillingAddressData.city;
        draft.billingProvince = currentBillingAddressData.province;
        draft.billingPostCode = currentBillingAddressData.postCode;
        draft.billingCountry = currentBillingAddressData.country;
      }

      if (isUpdateWithRegistrationAddress) {
        draft.registrationAddress1 = currentRegistrationAddressData.address;

        draft.registrationAdditionalAddress =
          currentRegistrationAddressData.additionalAddress;

        draft.registrationCity = currentRegistrationAddressData.city;
        draft.registrationProvince = currentRegistrationAddressData.province;
        draft.registrationPostalCode = currentRegistrationAddressData.postCode;
        draft.registrationCountry = currentRegistrationAddressData.country;
      }

      if (withConsents) {
        draft.consents = {
          ...Object.keys(consents).reduce((acc, cur) => {
            acc[cur] = JSON.parse(consents[cur]);
            return acc;
          }, {}),
          disclaimer: true,
          thirdPartiesProfiling: false,
        };
      }
    });

    const transformedDefaultValues = produce(defaultValues, draft => {
      delete draft.email;
      delete draft.deliveryContact;
      delete draft.deliveryData;
      delete draft.culture;
      delete draft.customerId;
      delete draft.brandIdToken;

      if (!isCompanyInformationVisible) {
        delete draft.companyName;
        delete draft.siretNumber;
      }

      if (!isCompanyInformationVisible || !isTVARelevantForSelectedCountry) {
        delete draft.TVANumber;
      }

      if (!isTINRelevantForSelectedCountry) {
        delete draft.tin;
      }

      if (!isUpdateWithDeliveryAddress) {
        delete draft.deliveryAdditionalAddress;
        delete draft.deliveryAddress1;
        delete draft.deliveryCity;
        delete draft.deliveryCountry;
        delete draft.deliveryPostCode;
        delete draft.deliveryProvince;
      }

      if (!isUpdateWithBillingAddress) {
        delete draft.billingAdditionalAddress;
        delete draft.billingAddress1;
        delete draft.billingCity;
        delete draft.billingCountry;
        delete draft.billingPostCode;
        delete draft.billingProvince;
      }

      if (!isUpdateWithRegistrationAddress) {
        delete draft.registrationAdditionalAddress;
        delete draft.registrationAddress1;
        delete draft.registrationCity;
        delete draft.registrationCountry;
        delete draft.registrationPostalCode;
        delete draft.registrationProvince;
      }
    });

    const areFieldsSame = equal(transformedDefaultValues, allFields);

    if (areFieldsSame) {
      return onSubmitSuccessCallback(allFields);
    }

    let resultUpdateData = null;

    try {
      resultUpdateData = await api.updateBrandIdData(allFields);
    } catch (error) {
      toast.error(t("notify.failedToUpdatePersonalData"), {}, error);

      return;
    }

    const { BRANDID_TOKEN_EXPIRED, OK } = BRANDID_RESPONSES;
    const { returnCode } = resultUpdateData.data ?? {};

    if (returnCode === BRANDID_TOKEN_EXPIRED) {
      window.location.href = getBrandIdLoginRedirect(
        returnUrlAfterLogin,
        brandIDConfig,
        language
      );

      return;
    }

    if (returnCode !== OK) {
      toast.error(
        t("notify.failedToUpdatePersonalData", {}, "Return code is not 'OK'")
      );

      return;
    }

    toast.success(t("notify.updateUserInfoSuccess"));

    return onSubmitSuccessCallback(allFields);
  };

  const areMultipleAddressesVisible =
    isDeliveryAddressVisible ||
    (isBillingAddressVisible && !isBillingAddressSame) ||
    isRegistrationAddressVisible;

  const handleRegistrationAddressSubmit = data => {
    setRegistrationAddressData(data);
    setIsEditingRegistrationAddress(false);
  };

  const RegistrationAddress = () => {
    return isRegistrationAddressVisible ? (
      <>
        {withDividers && !isReversedOrder && (
          <Divider className="bg-greyLight" />
        )}

        <Checkbox
          checked={isRegistrationAddressSame}
          className="my-4 ml-2"
          dataId="address-registration-same-radio"
          disabled={false}
          label={t("myDetails.inputs.sameRegistrationAddress")}
          onChange={handleRegistrationCheckboxChange}
        />

        {!isRegistrationAddressSame && (
          <Address
            data={registrationAddressData}
            isEditing={isEditingRegistrationAddress}
            onIsEditingChange={setIsEditingRegistrationAddress}
            onSubmit={handleRegistrationAddressSubmit}
            regexPatterns={addressRegexPatterns}
            text={t("myDetails.registrationAddressText")}
            title={t("myDetails.registrationAddressDetail")}
          />
        )}

        {withDividers && isReversedOrder && (
          <Divider className="bg-greyLight" />
        )}
      </>
    ) : null;
  };

  return (
    <>
      {isCompanyInformationVisible && (
        <>
          <CompanyInformation
            regexPatterns={companyInformationRegexPatterns}
            isEditing={isEditingCompanyInformation}
            data={companyInformationData}
            onIsEditingChange={setIsEditingCompanyInformation}
            onSubmit={data => {
              setCompanyInformationData(data);
              setIsEditingCompanyInformation(false);
            }}
            isTVANumberVisible={isTVARelevantForSelectedCountry}
          />

          {withDividers && <Divider className="bg-greyLight" />}
        </>
      )}
      <PersonalInformation
        regexPatterns={personalInformationRegexPatterns}
        data={produce(personalInformationData, draft => {
          draft.email = defaultValues.email;
        })}
        onSubmit={data => {
          setPersonalInformationData(data);
          setIsEditingPersonalInformation(false);
        }}
        isEditing={isEditingPersonalInformation}
        isEditButtonDisabled={isPersonalInformationEditButtonDisabled}
        onIsEditingChange={setIsEditingPersonalInformation}
        onChangeEmailButtonClick={onEmailButtonClick}
      />

      {withDividers && areMultipleAddressesVisible && (
        <Divider className="bg-greyLight" />
      )}
      {isDeliveryAddressVisible && (
        <>
          <Address
            regexPatterns={addressRegexPatterns}
            title={t("myDetails.deliveryAddressDetail")}
            isEditing={isEditingDeliveryAddress}
            onIsEditingChange={setIsEditingDeliveryAddress}
            data={deliveryAddressData}
            onSubmit={data => {
              setDeliveryAddressData(data);
              setIsEditingDeliveryAddress(false);
            }}
          />
          {withDividers && <Divider className="bg-greyLight" />}
        </>
      )}

      {isReversedOrder && <RegistrationAddress />}
      {isDeliveryAddressVisible && (
        <Checkbox
          className="my-4 ml-2"
          label={t("myDetails.inputs.sameBillingAddress")}
          checked={isBillingAddressSame}
          onChange={onBillingAddressCheckboxChange}
          disabled={false}
          dataId="address-billing-same-radio"
        />
      )}
      {isBillingAddressVisible && !isBillingAddressSame && (
        <Address
          regexPatterns={addressRegexPatterns}
          title={t("myDetails.billingAddressDetail")}
          text={t("myDetails.billingAddressText")}
          isEditing={isEditingBillingAddress}
          onIsEditingChange={setIsEditingBillingAddress}
          data={billingAddressData}
          onSubmit={data => {
            setBillingAddressData(data);
            setIsEditingBillingAddress(false);
          }}
        />
      )}
      {!isReversedOrder && <RegistrationAddress />}

      {withConsents && (
        <FormProvider {...formHook}>
          <CustomerConsents
            isReadOnly={!isConfirmationButtonVisible}
            onModifyClick={() => onEditingStart?.()}
          />
        </FormProvider>
      )}

      {isConfirmationButtonVisible && (
        <StickyRequestButton
          withBorder
          data-id="my-details-confirm"
          fixedHeaderHeight={fixedHeaderHeight}
          isStickyToTop={true}
          isStickyToBottom={true}
          disabled={
            isEditingPersonalInformation ||
            isEditingDeliveryAddress ||
            isEditingBillingAddress ||
            isEditingRegistrationAddress ||
            isPersonalInformationInvalid ||
            isCompanyInformationInvalid ||
            isDeliveryAddressInvalid ||
            isBillingAddressInvalid ||
            isRegistrationAddressInvalid
          }
          triggerRequestCallback={onSubmit}
        >
          {confirmationButtonLabel ?? t("myDetails.inputs.save")}
        </StickyRequestButton>
      )}

      <Modal
        ariaLabel={t("basket.delivery.postcodeNotMatchDealersModal")}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        withCloseIcon
      >
        <div className="flex flex-col gap-4 p-6">
          <Typography className="text-lg text-center">
            {t("basket.delivery.postcodeNotMatchDealersModal")}
          </Typography>
        </div>
      </Modal>
    </>
  );
};

UserDetailsContainer.propTypes = {
  onSubmitSuccessCallback: func.isRequired,
  returnUrlAfterLogin: string.isRequired,
  defaultValues: object.isRequired,
  onEmailButtonClick: func.isRequired,
  isCompanyInformationVisible: bool,
  isCompanyInformationRequired: bool,
  isDeliveryAddressRequired: bool,
  isDeliveryAddressVisible: bool,
  isRegistrationAddressRequired: bool,
  isRegistrationAddressVisible: bool,
  isBillingAddressRequired: bool,
  isBillingAddressVisible: bool,
  isConfirmationButtonVisible: bool,
  onEditingStart: func,
  withConsents: bool,
  withDividers: bool,
  isPersonalInformationEditButtonDisabled: bool,
  confirmationButtonLabel: string,
  fixedHeaderHeight: number,
};

export default UserDetailsContainer;
